import React, { useState, useEffect } from 'react';
import './App.css';
import { auth, provider, signInWithRedirect, signOut, signInWithPopup } from './firebase';
import { onAuthStateChanged, getRedirectResult } from "firebase/auth";

// התאריך של היום בפורמט yyyy-mm-dd
const todayDate = new Date().toISOString().split('T')[0];

// סטטוסים ברירת מחדל למשימות SEO
const defaultStatuses = [
  { name: "טרם התחיל", color: "#6b7280" },
  { name: "בעבודה", color: "#f59e0b" },
  { name: "בבדיקה", color: "#033f63" },
  { name: "הושלם", color: "#3c7c68" },
  { name: "נדחה", color: "#ae2c1e" }
];

function App() {
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDate, setNewDate] = useState(todayDate);
  const [newStatus, setNewStatus] = useState(defaultStatuses[0].name);
  const [newGroupId, setNewGroupId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  const [addingSubtaskToId, setAddingSubtaskToId] = useState(null);
  const [newSubtaskText, setNewSubtaskText] = useState('');
  const [newSubtaskDescription, setNewSubtaskDescription] = useState('');
  const [newSubtaskDate, setNewSubtaskDate] = useState(todayDate);
  const [newSubtaskStatus, setNewSubtaskStatus] = useState(defaultStatuses[0].name);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskParentId, setEditingSubtaskParentId] = useState(null);
  const [editSubtaskData, setEditSubtaskData] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  
  // ניהול סטטוסים מותאמים אישית
  const [customStatuses, setCustomStatuses] = useState([]);
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#6b7280');
  const [editingStatusId, setEditingStatusId] = useState(null);
  
  // סינון משימות לפי סטטוס
  const [statusFilter, setStatusFilter] = useState('הכל');
  
  // טאבים לפי זמן
  const [activeTab, setActiveTab] = useState('היום');
  
  // מקטעי משימות
  const [taskGroups, setTaskGroups] = useState([]);
  const [addingNewSection, setAddingNewSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  
  // תבניות מוכנות מראש
  const [showTemplates, setShowTemplates] = useState(false);
  
  // בחירה מרובה של משימות
  const [selectedTasks, setSelectedTasks] = useState([]);
  
  // ניהול פרויקטים ושמות
  const [projectName, setProjectName] = useState('רשימת המשימות');
  const [editingProjectName, setEditingProjectName] = useState(false);
  const [tempProjectName, setTempProjectName] = useState('');
  
  // מערכת פרויקטים
  const [projects, setProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState('default');
  const [newProjectName, setNewProjectName] = useState('');
  
  // סרגל צד מובייל
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  
  // עריכת שם פרויקט בסרגל
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingProjectNameTemp, setEditingProjectNameTemp] = useState('');

  // שילוב סטטוסים ברירת מחדל + מותאמים אישית
  const allStatuses = [...defaultStatuses, ...customStatuses];

  // תבניות מוכנות מראש
  const predefinedTemplates = [
    {
      id: 'seo-technical-audit',
      name: '🔧 אופטימיזציה טכנית מלאה',
      description: 'תבנית מלאה לביצוע אופטימיזציה טכנית לאתר - 15 צעדים מפורטים עם הוראות מקצועיות',
      sections: [
        {
          name: 'בדיקות טכניות בסיסיות',
          tasks: [
            {
              text: 'בדיקת זחילה ואינדקס ב-Search Console',
              description: 'ודא שהאתר נזחל כהלכה, בדוק עבור שגיאות זחילה ודפים שלא אינדקסו. פתח Search Console > Coverage ובדוק דוחות.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת robots.txt ו-sitemap.xml',
              description: 'ודא שה-robots.txt מוגדר נכון ולא חוסם דפים חשובים. בדוק שה-sitemap עדכני ומכיל את כל הדפים הרלוונטיים.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת SSL ואבטחת האתר',
              description: 'ודא שהאתר עובד עם HTTPS, בדוק תקינות תעודת SSL ווידוא שאין mixed content warnings.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'מבנה האתר וניווט',
          tasks: [
            {
              text: 'אופטימיזציה של מבנה URL',
              description: 'ודא שה-URLs ידידותיים, קצרים ומתארים את התוכן. הסר פרמטרים מיותרים ושפר את היררכיית הכתובות.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת ניווט פנימי ו-breadcrumbs',
              description: 'ודא שהניווט הפנימי לוגי ונגיש, הוסף breadcrumbs בכל הדפים ושפר את חוויית המשתמש.',
              status: 'טרם התחיל'
            },
            {
              text: 'אופטימיזציה של קישורים פנימיים',
              description: 'הוסף קישורים פנימיים רלוונטיים, ודא שהאנקור טקסט מתאר את הדף היעד ושפר את זרימת הכח בין הדפים.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'מהירות וביצועים',
          tasks: [
            {
              text: 'בדיקת מהירות טעינה ב-PageSpeed Insights',
              description: 'בדוק את מהירות הטעינה במובייל ובדסקטופ, זהה בעיות ביצועים ופתור אותן לפי המלצות Google.',
              status: 'טרם התחיל'
            },
            {
              text: 'אופטימיזציה של תמונות והדחסה',
              description: 'דחוס תמונות, השתמש ב-WebP, הוסף lazy loading ווידוא שכל התמונות ממוטבות לרזולוציות שונות.',
              status: 'טרם התחיל'
            },
            {
              text: 'מינימיזציה של CSS ו-JavaScript',
              description: 'דחוס קבצי CSS ו-JS, הסר קוד מיותר, השתמש בטכניקות caching ושפר את זמני הטעינה.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'תוכן ומחקר מילות מפתח',
          tasks: [
            {
              text: 'מחקר מילות מפתח מעודכן',
              description: 'בצע מחקר מילות מפתח מקיף עם Google Keyword Planner, זהה הזדמנויות חדשות ובדוק תחרותיות.',
              status: 'טרם התחיל'
            },
            {
              text: 'אופטימיזציה של כותרות H1-H6',
              description: 'ודא שכל דף יש כותרת H1 ייחודית, בנה היררכיית כותרות לוגית ושלב מילות מפתח באופן טבעי.',
              status: 'טרם התחיל'
            },
            {
              text: 'שיפור איכות התוכן והרלוונטיות',
              description: 'עדכן תוכן ישן, הוסף מידע רלוונטי, שפר את הקריאות ווידוא שהתוכן עונה על כוונת החיפוש.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'Meta Tags ו-Schema Markup',
          tasks: [
            {
              text: 'אופטימיזציה של Title Tags',
              description: 'ודא שכל דף יש Title ייחודי, אורך 50-60 תווים, מכיל מילת מפתח ראשית ומושך קליקים.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת Meta Descriptions מושכות',
              description: 'כתוב Meta Description של 150-160 תווים לכל דף, כלול מילות מפתח וקריאה לפעולה מושכת.',
              status: 'טרם התחיל'
            },
            {
              text: 'הוספת Schema Markup רלוונטי',
              description: 'הוסף structured data מתאים (Organization, LocalBusiness, Product וכו\'), בדוק תקינות ב-Schema Validator.',
              status: 'טרם התחיל'
            }
          ]
        }
      ]
    },
    {
      id: 'online-reputation-management',
      name: '🛡️ ניהול מוניטין (ORM)',
      description: 'תבנית מקיפה לניהול המוניטין הדיגיטלי - מעקב, ניתוח, וביצוע פעולות שיפור המוניטין באינטרנט',
      sections: [
        {
          name: 'מעקב ומוניטורינג',
          tasks: [
            {
              text: 'הגדרת התראות Google Alerts לשם החברה/מותג',
              description: 'הגדר התראות גוגל עבור שם החברה, שמות מנהלים, שמות מוצרים, וכל מילות מפתח רלוונטיות למותג.',
              status: 'טרם התחיל'
            },
            {
              text: 'מעקב אחר אזכורים ברשתות חברתיות',
              description: 'הגדר מעקב באמצעות כלים כמו Hootsuite או Mention לאזכורי המותג בפייסבוק, אינסטגרם, טוויטר, לינקדאין.',
              status: 'טרם התחיל'
            },
            {
              text: 'ניטור ביקורות ב-Google My Business ואתרי ביקורות',
              description: 'עקוב יומית אחר ביקורות ב-Google My Business, All Biz, Reviews ואתרי ביקורות רלוונטיים לתחום.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת תוצאות חיפוש לשם החברה בעמוד הראשון',
              description: 'בדוק יומית את תוצאות החיפוש לשם החברה ו-10 המילים הראשונות בגוגל לאיתור תוכן שלילי.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'ניתוח מצב נוכחי',
          tasks: [
            {
              text: 'הערכת המוניטין הדיגיטלי הנוכחי',
              description: 'בצע סקר מקיף של כל האזכורים הקיימים ברשת, דרג אותם לחיוביים/שליליים/ניטרליים וזהה מגמות.',
              status: 'טרם התחיל'
            },
            {
              text: 'ניתוח סנטימנט של אזכורים קיימים',
              description: 'השתמש בכלים כמו Brand24 או Google Alerts לניתוח טון האזכורים הקיימים ויצירת דוח מסכם.',
              status: 'טרם התחיל'
            },
            {
              text: 'זיהוי נקודות חוזק וחולשה במוניטין',
              description: 'צור רשימה של נושאים שמקבלים ביקורת חיובית ושלילית, זהה דפוסים וסיבות עיקריות.',
              status: 'טרם התחיל'
            },
            {
              text: 'מיפוי מתחרים ומוניטין שלהם',
              description: 'בדוק איך המתחרים העיקריים מתוארים ברשת ומה הם עושים נכון או לא נכון בניהול המוניטין.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'יצירת תוכן חיובי',
          tasks: [
            {
              text: 'יצירת תוכן איכותי באתר החברה',
              description: 'פרסם באופן קבוע מאמרים, סיפורי הצלחה, חדשות ועדכונים חיוביים באתר החברה.',
              status: 'טרם התחיל'
            },
            {
              text: 'פרסום מאמרים ברשתות מקצועיות',
              description: 'כתוב ופרסם מאמרי חוות דעת בלינקדאין, Medium, ואתרים מקצועיים אחרים בתחום.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת נוכחות חיובית ברשתות חברתיות',
              description: 'פרסם תוכן רגיל וחיובי, שתף הישגים, הודה ללקוחות, והראה את הצד האנושי של העסק.',
              status: 'טרם התחיל'
            },
            {
              text: 'פיתוח סיפורי הצלחה ומקרי בוחן',
              description: 'צור סיפורי הצלחה מפורטים של לקוחות, מקרי בוחן (case studies) ועדויות מאומתות.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'ניהול ביקורות',
          tasks: [
            {
              text: 'עידוד לקוחות מרוצים לכתוב ביקורות',
              description: 'צור מערכת לעידוד לקוחות מרוצים לכתוב ביקורות בגוגל ואתרי ביקורות רלוונטיים.',
              status: 'טרם התחיל'
            },
            {
              text: 'מענה מקצועי לביקורות חיוביות',
              description: 'הגב בצורה אישית ומקצועית לכל ביקורת חיובית, הודה ללקוח והזמן אותו לחזור.',
              status: 'טרם התחיל'
            },
            {
              text: 'טיפול בביקורות שליליות בצורה בונה',
              description: 'פתח תהליך סטנדרטי לטיפול בביקורות שליליות - הקשבה, התנצלות, פתרון, ומעקב.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת תהליך סטנדרטי לטיפול בביקורות',
              description: 'כתוב נוהל ברור ומפורט לכל העובדים איך לטפל בביקורות חיוביות ושליליות.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'תוכן מתקדם',
          tasks: [
            {
              text: 'יצירת מיניסייטים עם שם המותג בצורה חיובית',
              description: 'צור דפי נחיתה ומיניסייטים ממוקדים שיופיעו בתוצאות חיפוש ויציגו את המותג בצורה חיובית.',
              status: 'טרם התחיל'
            },
            {
              text: 'פיתוח כלים דיגיטליים חינמיים עם מיתוג החברה',
              description: 'צור מחשבונים, כלי בדיקה, או אפליקציות פשוטות שיספקו ערך ללקוחות ויחזקו את המותג.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת ספר דיגיטלי או מדריך מקצועי לחינם',
              description: 'כתוב וצור מדריך מקצועי או ספר דיגיטלי שמציג את המומחיות של החברה ומחזק את האמינות.',
              status: 'טרם התחיל'
            },
            {
              text: 'השקת פודקאסט או סדרת וידאו מקצועית',
              description: 'צור תוכן אודיו או וידאו קבוע שמציג את החברה כמובילה בתחום ומספק ערך לקהל היעד.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת דף ויקיפדיה מקצועי',
              description: 'אם החברה מתאימה, צור דף ויקיפדיה מקצועי ומאוזן שיופיע בתוצאות החיפוש הראשונות.',
              status: 'טרם התחיל'
            },
            {
              text: 'כתיבת מאמרי אורח באתרי חדשות ומקצועיים',
              description: 'כתוב מאמרי אורח באתרים מובילים בתחום, אתרי חדשות, ופורטלים עסקיים לחיזוק הסמכות.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת תוכן אינטראקטיבי',
              description: 'פתח סקרים, תחרויות, אתגרים דיגיטליים, ופעילויות אינטראקטיביות שמעודדות מעורבות חיובית.',
              status: 'טרם התחיל'
            },
            {
              text: 'השתתפות כמרצה בכנסים דיגיטליים ואירועי ענף',
              description: 'חפש הזדמנויות להרצות ולהשתתף בכנסים וירטואליים ואירועי ענף כדי לחזק את הנוכחות החיובית.',
              status: 'טרם התחיל'
            }
          ]
        }
      ]
    },
    {
      id: 'seo-site-migration',
      name: '🔄 מעבר לאתר חדש (SEO Migration)',
      description: 'תבנית מקיפה לביצוע מעבר בטוח לאתר חדש תוך שמירה על ביצועי SEO וטרפיק אורגני',
      sections: [
        {
          name: 'הכנה וחקירה מוקדמת',
          tasks: [
            {
              text: 'ביצוע גיבוי מלא של האתר הקיים',
              description: 'גיבוי של כל הקבצים, בסיס הנתונים, הגדרות שרת, ותעודות SSL לפני תחילת התהליך.',
              status: 'טרם התחיל'
            },
            {
              text: 'ניתוח מלא של ביצועי SEO הנוכחיים',
              description: 'תיעוד מפורט של דירוגים נוכחיים, טרפיק אורגני, מילות מפתח מובילות ונתוני Analytics.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת מפת אתר מלאה של הדפים הקיימים',
              description: 'מיפוי כל הדפים, מבנה URL, עמודים עם ביצועים גבוהים, ודפים עם בעיות טכניות.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת מהירות וביצועים של האתר הנוכחי',
              description: 'בדיקה מקיפה עם כלים כמו PageSpeed Insights, GTmetrix וWebPageTest כבסיס להשוואה.',
              status: 'טרם התחיל'
            },
            {
              text: 'ניתוח פרופיל קישורים וטרפיק רפרלים',
              description: 'מיפוי כל הקישורים הנכנסים, דומיינים מפנים, וזיהוי דפי נחיתה פופולריים.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'תכנון מבנה האתר החדש',
          tasks: [
            {
              text: 'תכנון מבנה URL חדש מותאם SEO',
              description: 'עיצוב מבנה URL ברור, הירכי ומותאם למנועי חיפוש עם קטגוריות לוגיות.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת מיפוי מפורט בין דפים ישנים לחדשים',
              description: 'מפת מעבר מלאה מכל URL ישן ל-URL חדש כולל החלטות על מיזוג או מחיקת דפים.',
              status: 'טרם התחיל'
            },
            {
              text: 'תכנון ארכיטקטורת מידע וניווט',
              description: 'עיצוב מבנה הניווט, תפריטים, קישורים פנימיים ומבנה ההיררכיה של האתר.',
              status: 'טרם התחיל'
            },
            {
              text: 'הגדרת מבנה כותרות (H1-H6) וסכמה',
              description: 'תכנון היררכיית כותרות, מטא תגיות, וסכמת structured data לכל סוג דף.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'הגדרות הפניות 301',
          tasks: [
            {
              text: 'יצירת קובץ הפניות 301 מקיף',
              description: 'כתיבת קובץ .htaccess או הגדרות שרת עם הפניות 301 מכל URL ישן לחדש.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת תקינות כל ההפניות במערכת הסטגינג',
              description: 'בדיקה יסודית של כל הפניה לוודא שהיא פועלת ומובילה לדף הנכון ללא שגיאות.',
              status: 'טרם התחיל'
            },
            {
              text: 'טיפול בדפים שאין להם תחליף באתר החדש',
              description: 'החלטה על הפניה לדף רלוונטי או יצירת דף 410 Gone עבור תוכן שהוסר לצמיתות.',
              status: 'טרם התחיל'
            },
            {
              text: 'הגדרת הפניות לקבצי מדיה וחומרים ישנים',
              description: 'וידוא שכל התמונות, מסמכים וקבצי מדיה מופנים נכון לחלופות החדשות.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'אופטימיזציה טכנית',
          tasks: [
            {
              text: 'אופטימיזציה של זמני טעינה ומהירות האתר',
              description: 'דחיסת תמונות, מינימיזציה של CSS/JS, הגדרת caching ואופטימיזציה של שרת.',
              status: 'טרם התחיל'
            },
            {
              text: 'הטמעת Schema Markup מתקדם',
              description: 'הוספת structured data רלוונטי לכל סוגי הדפים - מוצרים, מאמרים, חברה, FAQ.',
              status: 'טרם התחיל'
            },
            {
              text: 'אופטימיזציה לניידים ו-Core Web Vitals',
              description: 'וידוא שהאתר מותאם למובייל עם ביצועים מצוינים ב-LCP, FID ו-CLS.',
              status: 'טרם התחיל'
            },
            {
              text: 'הגדרת תעודות SSL ואבטחת האתר',
              description: 'התקנת תעודת SSL, הגדרת HTTPS והפניה אוטומטית מ-HTTP ל-HTTPS.',
              status: 'טרם התחיל'
            },
            {
              text: 'יצירת ועדכון קבצי XML Sitemap',
              description: 'יצירת sitemap מעודכן לכל סוגי התוכן והגשתו למנועי החיפוש.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'ביצוע המעבר',
          tasks: [
            {
              text: 'תיאום עם ספק האחסון לביצוע המעבר',
              description: 'תכנון זמני השבתה, גיבויים נוספים, והכנת תוכנית חירום למקרה של בעיות.',
              status: 'טרם התחיל'
            },
            {
              text: 'העלאת האתר החדש לשרת הייצור',
              description: 'העלאה מדורגת של קבצים, בסיס נתונים, והגדרות עם בדיקות ביניים.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת תקינות כל הפונקציות המרכזיות',
              description: 'בדיקה מקיפה של טפסים, עגלת קניות, חיפוש, התחברות ופונקציות חיוניות.',
              status: 'טרם התחיל'
            },
            {
              text: 'עדכון הגדרות Google Search Console',
              description: 'הוספת נכס חדש ב-GSC, הגשת sitemap החדש, והגדרת התראות לשגיאות.',
              status: 'טרם התחיל'
            },
            {
              text: 'עדכון Google Analytics ו-Tag Manager',
              description: 'הגדרת מעקב חדש, העברת goals וecommerce tracking, ובדיקת נתוני מעקב.',
              status: 'טרם התחיל'
            }
          ]
        },
        {
          name: 'מעקב ובקרה',
          tasks: [
            {
              text: 'ניטור יומי של שגיאות זחילה ואינדוקס',
              description: 'בדיקה יומית ב-GSC לשגיאות 404, בעיות זחילה, ושגיאות אינדוקס חדשות.',
              status: 'טרם התחיל'
            },
            {
              text: 'מעקב אחר שינויים בדירוגים ובטרפיק',
              description: 'ניטור יומי של עמדות במילות מפתח מרכזיות ונתוני טרפיק אורגני.',
              status: 'טרם התחיל'
            },
            {
              text: 'בדיקת תקינות הקישורים הפנימיים והחיצוניים',
              description: 'סריקה שבועית לזיהוי קישורים שבורים, הפניות לא תקינות ותיקונם.',
              status: 'טרם התחיל'
            },
            {
              text: 'ניתוח נתוני מהירות וביצועים',
              description: 'מעקב שבועי אחר זמני טעינה, Core Web Vitals, ושיפורים נדרשים.',
              status: 'טרם התחיל'
            },
            {
              text: 'דיווח חודשי על ביצועי המעבר',
              description: 'יצירת דוח מקיף עם השוואה לנתונים הקודמים, הישגים ותחומי שיפור.',
              status: 'טרם התחיל'
            }
          ]
        }
      ]
    }
  ];

  // פונקציות לחישוב תאריכים
  const getDateRanges = () => {
    const today = new Date().toISOString().split('T')[0];
    const todayDate = new Date(today);
    
    // תחילת השבוע הנוכחי (יום ראשון)
    const currentWeekStart = new Date(todayDate);
    currentWeekStart.setDate(todayDate.getDate() - todayDate.getDay());
    
    // סוף השבוע הנוכחי (שבת)
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    
    // תחילת השבוע הבא
    const nextWeekStart = new Date(currentWeekEnd);
    nextWeekStart.setDate(currentWeekEnd.getDate() + 1);
    
    // סוף החודש הנוכחי
    const currentMonthEnd = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0);
    
    // תחילת החודש הבא
    const nextMonthStart = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 1);
    
    return {
      today: today,
      currentWeekStart: currentWeekStart.toISOString().split('T')[0],
      currentWeekEnd: currentWeekEnd.toISOString().split('T')[0],
      nextWeekStart: nextWeekStart.toISOString().split('T')[0],
      currentMonthEnd: currentMonthEnd.toISOString().split('T')[0],
      nextMonthStart: nextMonthStart.toISOString().split('T')[0]
    };
  };

  // פונקציה לבדיקה אם משימה באיחור
  const isTaskOverdue = (taskDate) => {
    if (!taskDate) return false;
    const today = new Date().toISOString().split('T')[0];
    return taskDate < today;
  };

  // פונקציה לקבלת ימי איחור
  const getOverdueDays = (taskDate) => {
    if (!taskDate) return 0;
    const today = new Date();
    const taskDateObj = new Date(taskDate);
    const diffTime = today - taskDateObj;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // פונקציה לסינון משימות לפי טאב
  const getTasksByTab = (tab) => {
    const { today, currentWeekEnd, nextWeekStart, currentMonthEnd, nextMonthStart } = getDateRanges();
    
    return tasks.filter(task => {
      const taskDate = task.dueDate || today; // ברירת מחדל היום אם אין תאריך
      
      switch (tab) {
        case 'היום':
          return taskDate <= today; // היום + באיחור + ללא תאריך
        case 'השבוע':
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          const tomorrowStr = tomorrow.toISOString().split('T')[0];
          return taskDate >= tomorrowStr && taskDate <= currentWeekEnd;
        case 'החודש הקרוב':
          return taskDate >= nextWeekStart && taskDate <= currentMonthEnd;
        case 'חודש הבא ואילך':
          return taskDate >= nextMonthStart;
        default:
          return true;
      }
    });
  };

  // פונקציה לסינון משימות לפי סטטוס וטאב
  const getFilteredTasks = () => {
    const tabTasks = getTasksByTab(activeTab);
    
    if (statusFilter === 'הכל') {
      return tabTasks;
    }
    return tabTasks.filter(task => task.status === statusFilter);
  };

  // פונקציה לקבלת כמות משימות לפי טאב
  const getTaskCountByTab = (tab) => {
    return getTasksByTab(tab).length;
  };

  // פונקציות לניהול מקטעי משימות
  const addNewSection = () => {
    const sectionName = newSectionName.trim() || 'רשימת המשימות';
    
    const newSection = {
      id: Date.now(),
      name: sectionName,
      order: taskGroups.length
    };

    setTaskGroups(prev => [...prev, newSection]);
    setNewSectionName('');
    setAddingNewSection(false);
  };

  const cancelAddSection = () => {
    setAddingNewSection(false);
    setNewSectionName('');
  };

  const deleteSection = (sectionId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את המקטע הזה? המשימות יעברו למקטע הראשי.')) {
      setTaskGroups(prev => prev.filter(g => g.id !== sectionId));
      // העבר משימות למקטע הראשי
      setTasks(prev => prev.map(task => 
        task.groupId && task.groupId.toString() === sectionId.toString() 
          ? { ...task, groupId: null } 
          : task
      ));
    }
  };

  const startEditSection = (section) => {
    setEditingSectionId(section.id);
    setEditingSectionName(section.name);
  };

  const saveEditSection = () => {
    if (!editingSectionName.trim()) return;

    setTaskGroups(prev => 
      prev.map(g => 
        g.id === editingSectionId 
          ? { ...g, name: editingSectionName.trim() }
          : g
      )
    );

    setEditingSectionId(null);
    setEditingSectionName('');
  };

  const cancelEditSection = () => {
    setEditingSectionId(null);
    setEditingSectionName('');
  };

  // פונקציה ליצירת משימות מתבנית
  const createFromTemplate = (template) => {
    if (!user) return;
    
    let maxTaskId = tasks.length ? Math.max(...tasks.map(t => t.id)) : 0;
    let maxGroupId = taskGroups.length ? Math.max(...taskGroups.map(g => g.id)) : 0;
    
    const newGroups = [];
    const newTasks = [];
    
    // יצירת מקטעים ומשימות מהתבנית
    template.sections.forEach((section, sectionIndex) => {
      const groupId = maxGroupId + sectionIndex + 1;
      
      // יצירת מקטע
      newGroups.push({
        id: groupId,
        name: section.name,
        order: taskGroups.length + sectionIndex
      });
      
      // יצירת משימות למקטע
      section.tasks.forEach((taskTemplate, taskIndex) => {
        const taskId = maxTaskId + (sectionIndex * 10) + taskIndex + 1;
        
        newTasks.push({
          id: taskId,
          text: taskTemplate.text,
          description: taskTemplate.description,
          status: taskTemplate.status,
          done: false,
          dueDate: todayDate,
          groupId: groupId,
          subtasks: []
        });
      });
    });
    
    // הוספת המקטעים והמשימות החדשים
    setTaskGroups(prev => [...prev, ...newGroups]);
    setTasks(prev => [...prev, ...newTasks]);
    setShowTemplates(false);
    
    alert(`✅ תבנית "${template.name}" נוצרה בהצלחה! נוספו ${newGroups.length} מקטעים ו-${newTasks.length} משימות.\n💡 עכשיו תוכל לערוך את התאריכים בהתאם לצרכים שלך.`);
  };

  // פונקציה לקבוצת משימות לפי קבוצות
  const getTasksGrouped = () => {
    const filteredTasks = getFilteredTasks();
    const grouped = {};
    
    // מקטע ראשי למשימות ללא מקטע
    grouped['main'] = {
      id: 'main',
      name: 'משימות ראשיות',
      tasks: filteredTasks.filter(task => !task.groupId)
    };
    
    // מקטעים מותאמים אישית
    taskGroups.forEach(group => {
      const groupTasks = filteredTasks.filter(task => task.groupId && task.groupId.toString() === group.id.toString());
      
      grouped[group.id] = {
        ...group,
        tasks: groupTasks
      };
    });
    
    return grouped;
  };

  // הפעלת/כיבוי Dark Mode
  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  // החלת Dark Mode על הbody
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  // 🆕 מאזין להתחברות גם אחרי רענון + טיפול בredirect
  useEffect(() => {
    console.log('🔥 Auth effect starting...');
    
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log('🔥 Auth state changed:', currentUser ? currentUser.displayName : 'No user');
      console.log('🔥 Full user object:', currentUser);
      setUser(currentUser);
      setLoading(false);
    });
    
    // טיפול בתוצאה של redirect
    console.log('🔥 Checking redirect result...');
    getRedirectResult(auth).then((result) => {
      console.log('🔥 Redirect result:', result);
      if (result && result.user) {
        console.log('🔥 Redirect login successful:', result.user.displayName);
        console.log('🔥 Redirect user object:', result.user);
        setUser(result.user);
      } else {
        console.log('🔥 No redirect result or user');
      }
    }).catch((error) => {
      console.error('🔥 Redirect error:', error);
      console.error('🔥 Error code:', error.code);
      console.error('🔥 Error message:', error.message);
    }).finally(() => {
      console.log('🔥 Setting loading to false');
      setLoading(false);
    });
    
    return () => {
      console.log('🔥 Cleaning up auth listener');
      unsubscribe();
    };
  }, []);

  // התחברות עם popup + fallback לredirect
  const handleLogin = async () => {
    try {
      console.log('🔥 Starting popup login...');
      console.log('🔥 Auth object:', auth);
      console.log('🔥 Provider object:', provider);
      setLoading(true);
      
      // מנסים popup קודם
      const result = await signInWithPopup(auth, provider);
      console.log('🔥 Popup login successful:', result.user.displayName);
      setUser(result.user);
      setLoading(false);
      
    } catch (err) {
      console.error('🔥 Popup failed, trying redirect...', err);
      
      // אם popup נכשל, מנסים redirect
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        try {
          console.log('🔥 Starting redirect login as fallback...');
          await signInWithRedirect(auth, provider);
          console.log('🔥 Redirect initiated successfully');
        } catch (redirectErr) {
          console.error('🔥 שגיאה גם ב-redirect:', redirectErr);
          setLoading(false);
          alert('שגיאה בהתחברות: ' + redirectErr.message);
        }
      } else {
        console.error('🔥 שגיאה בהתחברות:', err);
        console.error('🔥 Error code:', err.code);
        console.error('🔥 Error message:', err.message);
        setLoading(false);
        alert('שגיאה בהתחברות: ' + err.message);
      }
    }
  };

  // התנתקות
  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setTasks([]);
  };



  // שמירת משימות לפי משתמש ופרויקט
  useEffect(() => {
    if (user && currentProjectId) {
      localStorage.setItem(`tasks_${user.uid}_${currentProjectId}`, JSON.stringify(tasks));
      localStorage.setItem(`currentProject_${user.uid}`, currentProjectId);
    }
  }, [tasks, user, currentProjectId]);

  // טעינת סטטוסים מותאמים אישית מ-localStorage
  useEffect(() => {
    if (user) {
      const savedStatuses = localStorage.getItem(`customStatuses_${user.uid}`);
      if (savedStatuses) {
        setCustomStatuses(JSON.parse(savedStatuses));
      }
    }
  }, [user]);

  // שמירת סטטוסים מותאמים אישית ב-localStorage
  useEffect(() => {
    if (user && customStatuses.length > 0) {
      localStorage.setItem(`customStatuses_${user.uid}`, JSON.stringify(customStatuses));
    }
  }, [customStatuses, user]);



  // שמירת מקטעי משימות ב-localStorage לפי פרויקט
  useEffect(() => {
    if (user && currentProjectId) {
      localStorage.setItem(`taskGroups_${user.uid}_${currentProjectId}`, JSON.stringify(taskGroups));
    }
  }, [taskGroups, user, currentProjectId]);

  // טעינת פרויקטים ונתונים מ-localStorage
  useEffect(() => {
    if (user) {
      // טעינת רשימת הפרויקטים
      const savedProjects = localStorage.getItem(`projects_${user.uid}`);
      if (savedProjects) {
        setProjects(JSON.parse(savedProjects));
      }
      
      // טעינת הפרויקט הנוכחי
      const savedCurrentProject = localStorage.getItem(`currentProject_${user.uid}`);
      const projectIdToLoad = savedCurrentProject || 'default';
      setCurrentProjectId(projectIdToLoad);
      
      // טעינת נתונים של הפרויקט הנוכחי
      const savedTasks = localStorage.getItem(`tasks_${user.uid}_${projectIdToLoad}`);
      const savedGroups = localStorage.getItem(`taskGroups_${user.uid}_${projectIdToLoad}`);
      const savedProjectName = localStorage.getItem(`projectName_${user.uid}_${projectIdToLoad}`);
      
      if (savedTasks) {
        const loadedTasks = JSON.parse(savedTasks);
        const tasksWithStatusAndSubtasks = loadedTasks.map(task => ({
          ...task,
          status: task.status || defaultStatuses[0].name,
          subtasks: task.subtasks || []
        }));
        setTasks(tasksWithStatusAndSubtasks);
      }
      
      if (savedGroups) {
        setTaskGroups(JSON.parse(savedGroups));
      }
      
      setProjectName(savedProjectName || 'רשימת המשימות');
    }
  }, [user]);

  // שמירת שם הפרויקט ב-localStorage
  useEffect(() => {
    if (user && currentProjectId && projectName) {
      localStorage.setItem(`projectName_${user.uid}_${currentProjectId}`, projectName);
    }
  }, [projectName, user, currentProjectId]);

  const addTask = () => {
    console.log('Adding task with status:', newStatus);
    if (!user || newTask.trim() === '') return;
    const newId = tasks.length ? Math.max(...tasks.map(t => t.id)) + 1 : 1;
    const newTaskObj = {
      id: newId,
      text: newTask.trim(),
      description: newDescription.trim(),
      status: newStatus,
      done: false,
      dueDate: newDate || todayDate,
      groupId: newGroupId,
      subtasks: [] // מערך תת-משימות
    };
    console.log('New task object:', newTaskObj);
    setTasks([
      ...tasks,
      newTaskObj
    ]);
    setNewTask('');
    setNewDescription('');
    setNewStatus(defaultStatuses[0].name);
    setNewDate(todayDate);
    setNewGroupId(null);
  };



  const deleteTask = (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את המשימה הזו?')) {
      setTasks(prev => prev.filter(task => task.id !== id));
    }
  };

  const startEdit = (task) => {
    setEditingId(task.id);
    setEditData({ 
      ...task,
      status: task.status || defaultStatuses[0].name // וידוא שיש סטטוס בעריכה
    });
    setExpandedTaskId(null); // סגירת האקורדיון כשעורכים
    clearSelection(); // ניקוי בחירה כשמתחילים לערוך
  };

  const saveEdit = () => {
    console.log('Saving edit with data:', editData);
    setTasks(prev =>
      prev.map(task =>
        task.id === editingId ? { 
          ...editData,
          subtasks: task.subtasks || [] // שמירת תת-משימות קיימות
        } : task
      )
    );
    setEditingId(null);
    setEditData({});
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  // פונקציה לחיתוך תיאור ל-4-5 מילים ראשונות
  const truncateDescription = (description, wordLimit = 5) => {
    if (!description) return '';
    const words = description.trim().split(' ');
    if (words.length <= wordLimit) return description;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  // פונקציה להחלפת מצב הרחבה של משימה
  const toggleTaskExpansion = (taskId) => {
    setExpandedTaskId(expandedTaskId === taskId ? null : taskId);
  };

  // פונקציה לקבלת צבע לפי סטטוס
  const getStatusColor = (status) => {
    const statusObj = allStatuses.find(s => s.name === status);
    return statusObj ? statusObj.color : '#6b7280';
  };

  // פונקציות לניהול סטטוסים מותאמים אישית
  const addCustomStatus = () => {
    if (!newStatusName.trim()) return;
    
    // בדיקה שהסטטוס לא קיים כבר
    const exists = allStatuses.find(s => s.name === newStatusName.trim());
    if (exists) {
      alert('סטטוס זה כבר קיים!');
      return;
    }

    const newStatus = {
      id: Date.now(),
      name: newStatusName.trim(),
      color: newStatusColor
    };

    setCustomStatuses(prev => [...prev, newStatus]);
    setNewStatusName('');
    setNewStatusColor('#6b7280');
  };

  const deleteCustomStatus = (statusId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את הסטטוס הזה?')) {
      setCustomStatuses(prev => prev.filter(s => s.id !== statusId));
    }
  };

  const startEditStatus = (status) => {
    setEditingStatusId(status.id);
    setNewStatusName(status.name);
    setNewStatusColor(status.color);
  };

  const saveEditStatus = () => {
    if (!newStatusName.trim()) return;

    setCustomStatuses(prev => 
      prev.map(s => 
        s.id === editingStatusId 
          ? { ...s, name: newStatusName.trim(), color: newStatusColor }
          : s
      )
    );

    setEditingStatusId(null);
    setNewStatusName('');
    setNewStatusColor('#6b7280');
  };

  const cancelEditStatus = () => {
    setEditingStatusId(null);
    setNewStatusName('');
    setNewStatusColor('#6b7280');
  };

  // פונקציה לשמירת סטטוס חדש כשמשתמש מקליד משהו שלא קיים
  const handleStatusChange = (value) => {
    setNewStatus(value);
    
    // אם הסטטוס לא קיים, הוסף אותו אוטומטית
    const exists = allStatuses.find(s => s.name === value.trim());
    if (!exists && value.trim() && value.trim().length > 0) {
      const autoStatus = {
        id: Date.now(),
        name: value.trim(),
        color: '#6b7280' // צבע ברירת מחדל
      };
      setCustomStatuses(prev => [...prev, autoStatus]);
    }
  };

  // פונקציות לניהול תת-משימות
  const addSubtask = (parentTaskId) => {
    if (!newSubtaskText.trim()) return;
    
    setTasks(prev => prev.map(task => {
      if (task.id === parentTaskId) {
        const subtaskId = task.subtasks?.length ? Math.max(...task.subtasks.map(st => st.id)) + 1 : 1;
        const newSubtask = {
          id: subtaskId,
          text: newSubtaskText.trim(),
          description: newSubtaskDescription.trim(),
          status: newSubtaskStatus,
          done: false,
          dueDate: newSubtaskDate || todayDate,
          parentId: parentTaskId
        };
        return {
          ...task,
          subtasks: [...(task.subtasks || []), newSubtask]
        };
      }
      return task;
    }));
    
    setNewSubtaskText('');
    setNewSubtaskDescription('');
    setNewSubtaskDate(todayDate);
    setNewSubtaskStatus(defaultStatuses[0].name);
    setAddingSubtaskToId(null);
  };

  const toggleSubtask = (parentTaskId, subtaskId) => {
    setTasks(prev => prev.map(task => {
      if (task.id === parentTaskId) {
        return {
          ...task,
          subtasks: task.subtasks?.map(subtask =>
            subtask.id === subtaskId ? { ...subtask, done: !subtask.done } : subtask
          ) || []
        };
      }
      return task;
    }));
  };

  const deleteSubtask = (parentTaskId, subtaskId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את תת-המשימה הזו?')) {
      setTasks(prev => prev.map(task => {
        if (task.id === parentTaskId) {
          return {
            ...task,
            subtasks: task.subtasks?.filter(subtask => subtask.id !== subtaskId) || []
          };
        }
        return task;
      }));
    }
  };

  const startAddSubtask = (taskId) => {
    setAddingSubtaskToId(taskId);
    setNewSubtaskText('');
    setNewSubtaskDescription('');
    setNewSubtaskDate(todayDate);
    setNewSubtaskStatus(defaultStatuses[0].name);
  };

  const cancelAddSubtask = () => {
    setAddingSubtaskToId(null);
    setNewSubtaskText('');
    setNewSubtaskDescription('');
    setNewSubtaskDate(todayDate);
    setNewSubtaskStatus(defaultStatuses[0].name);
  };

  const startEditSubtask = (parentTaskId, subtask) => {
    setEditingSubtaskId(subtask.id);
    setEditingSubtaskParentId(parentTaskId);
    setEditSubtaskData({
      ...subtask,
      status: subtask.status || defaultStatuses[0].name,
      dueDate: subtask.dueDate || todayDate,
      description: subtask.description || ''
    });
  };

  const saveEditSubtask = () => {
    setTasks(prev => prev.map(task => {
      if (task.id === editingSubtaskParentId) {
        return {
          ...task,
          subtasks: task.subtasks?.map(subtask =>
            subtask.id === editingSubtaskId ? {
              ...editSubtaskData,
              parentId: editingSubtaskParentId
            } : subtask
          ) || []
        };
      }
      return task;
    }));
    
    setEditingSubtaskId(null);
    setEditingSubtaskParentId(null);
    setEditSubtaskData({});
  };

  const cancelEditSubtask = () => {
    setEditingSubtaskId(null);
    setEditingSubtaskParentId(null);
    setEditSubtaskData({});
    };

  // פונקציות לניהול בחירה מרובה
  const toggleSelectTask = (taskId) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const selectAllVisibleTasks = () => {
    const visibleTasks = getFilteredTasks();
    const allSelected = visibleTasks.every(task => selectedTasks.includes(task.id));
    
    if (allSelected) {
      // אם הכל נבחר, בטל בחירה
      setSelectedTasks(prev => prev.filter(id => !visibleTasks.some(task => task.id === id)));
    } else {
      // אחרת, בחר את כל המשימות הגלויות
      const visibleTaskIds = visibleTasks.map(task => task.id);
      setSelectedTasks(prev => [...new Set([...prev, ...visibleTaskIds])]);
    }
  };

  const clearSelection = () => {
    setSelectedTasks([]);
  };

  const bulkDeleteTasks = () => {
    if (selectedTasks.length === 0) return;
    
    const confirmMessage = `האם אתה בטוח שברצונך למחוק ${selectedTasks.length} משימות נבחרות?`;
    if (window.confirm(confirmMessage)) {
      setTasks(prev => prev.filter(task => !selectedTasks.includes(task.id)));
      clearSelection();
    }
  };

  const bulkUpdateStatus = (newStatus) => {
    if (selectedTasks.length === 0) return;
    
    setTasks(prev => prev.map(task => 
      selectedTasks.includes(task.id) 
        ? { ...task, status: newStatus }
        : task
    ));
    clearSelection();
  };

  const bulkUpdateDate = (newDate) => {
    if (selectedTasks.length === 0) return;
    
    setTasks(prev => prev.map(task => 
      selectedTasks.includes(task.id) 
        ? { ...task, dueDate: newDate }
        : task
    ));
    clearSelection();
  };
  
  // פונקציות לניהול שם הפרויקט
  const startEditProjectName = () => {
    setTempProjectName(projectName);
    setEditingProjectName(true);
  };

  const saveProjectName = () => {
    if (tempProjectName.trim()) {
      setProjectName(tempProjectName.trim());
      if (user && currentProjectId) {
        localStorage.setItem(`projectName_${user.uid}_${currentProjectId}`, tempProjectName.trim());
      }
    }
    setEditingProjectName(false);
    setTempProjectName('');
  };

  const cancelEditProjectName = () => {
    setEditingProjectName(false);
    setTempProjectName('');
  };

  // פונקציות לניהול פרויקטים
  const createNewProject = () => {
    if (!user) return;
    
    const projectName = newProjectName.trim() || 'רשימת המשימות';
    
    const newProject = {
      id: Date.now().toString(),
      name: projectName,
      createdAt: new Date().toISOString(),
      tasksCount: 0
    };
    
    setProjects(prev => [...prev, newProject]);
    setNewProjectName('');
    setCreatingProject(false);
    
    // שמירה בlocalStorage
    const updatedProjects = [...projects, newProject];
    localStorage.setItem(`projects_${user.uid}`, JSON.stringify(updatedProjects));
    
    // עבור לפרויקט החדש
    switchToProject(newProject.id);
  };

  const switchToProject = (projectId) => {
    // שמירת הנתונים הנוכחיים לפני המעבר
    if (user) {
      localStorage.setItem(`tasks_${user.uid}_${currentProjectId}`, JSON.stringify(tasks));
      localStorage.setItem(`taskGroups_${user.uid}_${currentProjectId}`, JSON.stringify(taskGroups));
      localStorage.setItem(`projectName_${user.uid}_${currentProjectId}`, projectName);
    }
    
    // מעבר לפרויקט החדש
    setCurrentProjectId(projectId);
    
    // טעינת נתונים של הפרויקט החדש
    if (user) {
      const savedTasks = localStorage.getItem(`tasks_${user.uid}_${projectId}`);
      const savedGroups = localStorage.getItem(`taskGroups_${user.uid}_${projectId}`);
      const savedProjectName = localStorage.getItem(`projectName_${user.uid}_${projectId}`);
      
      setTasks(savedTasks ? JSON.parse(savedTasks) : []);
      setTaskGroups(savedGroups ? JSON.parse(savedGroups) : []);
      setProjectName(savedProjectName || 'רשימת המשימות');
    }
    
    // איפוס בחירה ומצבים
    clearSelection();
    setActiveTab('היום');
    setStatusFilter('הכל');
    setSidebarOpen(false); // סגירת הסרגל במובייל
  };

  const deleteProject = (projectId) => {
    if (projectId === 'default') {
      alert('לא ניתן למחוק את הפרויקט הברירת מחדל');
      return;
    }
    
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    
    const confirmMessage = `האם אתה בטוח שברצונך למחוק את הפרויקט "${project.name}"? כל המשימות והנתונים יאבדו!`;
    if (!window.confirm(confirmMessage)) return;
    
    // מחיקת הפרויקט מהרשימה
    setProjects(prev => prev.filter(p => p.id !== projectId));
    
    // מחיקת נתונים מlocalStorage
    if (user) {
      localStorage.removeItem(`tasks_${user.uid}_${projectId}`);
      localStorage.removeItem(`taskGroups_${user.uid}_${projectId}`);
      localStorage.removeItem(`projectName_${user.uid}_${projectId}`);
      
      // עדכון רשימת הפרויקטים בlocalStorage
      const updatedProjects = projects.filter(p => p.id !== projectId);
      localStorage.setItem(`projects_${user.uid}`, JSON.stringify(updatedProjects));
    }
    
    // מעבר לפרויקט ברירת מחדל אם זה היה הפרויקט הנוכחי
    if (currentProjectId === projectId) {
      switchToProject('default');
    }
  };

  const getCurrentProject = () => {
    if (currentProjectId === 'default') {
      return { id: 'default', name: projectName, tasksCount: tasks.length };
    }
    return projects.find(p => p.id === currentProjectId) || 
           { id: currentProjectId, name: projectName, tasksCount: tasks.length };
  };

  // פונקציות סרגל צד
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const startCreatingProject = () => {
    setCreatingProject(true);
    setNewProjectName('רשימת המשימות');
  };

  const cancelCreatingProject = () => {
    setCreatingProject(false);
    setNewProjectName('');
  };

  const getProjectTaskCount = (projectId) => {
    if (projectId === currentProjectId) {
      return tasks.length;
    }
    const savedTasks = localStorage.getItem(`tasks_${user.uid}_${projectId}`);
    return savedTasks ? JSON.parse(savedTasks).length : 0;
  };

  // פונקציה לקבלת צבע ייחודי לפרויקט
  const getProjectColor = (index) => {
    const colors = [
      '#6366f1', '#8b5cf6', '#06b6d4', '#3c7c68', 
      '#f59e0b', '#ef4444', '#ec4899', '#3c7c68',
      '#3c7c68', '#f97316', '#3b82f6', '#a855f7'
    ];
    return colors[index % colors.length];
  };

  // פונקציות לעריכת שם פרויקט בסרגל
  const startEditSidebarProject = (project) => {
    setEditingProjectId(project.id);
    setEditingProjectNameTemp(project.name);
  };

  const saveSidebarProjectName = () => {
    if (!editingProjectNameTemp.trim()) return;
    
    // עדכון הפרויקט ברשימה
    setProjects(prev => prev.map(p => 
      p.id === editingProjectId 
        ? { ...p, name: editingProjectNameTemp.trim() }
        : p
    ));
    
    // שמירה בlocalStorage
    if (user) {
      const updatedProjects = projects.map(p => 
        p.id === editingProjectId 
          ? { ...p, name: editingProjectNameTemp.trim() }
          : p
      );
      localStorage.setItem(`projects_${user.uid}`, JSON.stringify(updatedProjects));
      
      // אם זה הפרויקט הנוכחי, עדכן גם את שם הפרויקט הנוכחי
      if (currentProjectId === editingProjectId) {
        setProjectName(editingProjectNameTemp.trim());
        localStorage.setItem(`projectName_${user.uid}_${editingProjectId}`, editingProjectNameTemp.trim());
      }
    }
    
    setEditingProjectId(null);
    setEditingProjectNameTemp('');
  };

  const cancelEditSidebarProject = () => {
    setEditingProjectId(null);
    setEditingProjectNameTemp('');
  };

  // ניהול התראות
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // טוען התראות מ-localStorage
  useEffect(() => {
    if (user) {
      const saved = localStorage.getItem(`notifications_${user.uid}`);
      if (saved) setNotifications(JSON.parse(saved));
    }
  }, [user]);

  // שמירת התראות ב-localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(`notifications_${user.uid}` , JSON.stringify(notifications));
    }
  }, [notifications, user]);

  // יצירת התראות חדשות על בסיס משימות
  useEffect(() => {
    if (!user) return;
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const todayStr = today.toISOString().split('T')[0];
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    let newNotifs = [];
    tasks.forEach(task => {
      if (!task.dueDate || task.done) return;
      // באיחור
      if (task.dueDate < todayStr) {
        if (!notifications.some(n => n.taskId === task.id && n.type === 'overdue')) {
          newNotifs.push({
            id: `notif-${task.id}-overdue`,
            type: 'overdue',
            taskId: task.id,
            title: 'משימה באיחור!',
            message: `המשימה "${task.text}" באיחור!`,
            date: new Date().toISOString(),
            read: false
          });
        }
      }
      // מתקרב לדדליין
      else if (task.dueDate === tomorrowStr) {
        if (!notifications.some(n => n.taskId === task.id && n.type === 'deadline_soon')) {
          newNotifs.push({
            id: `notif-${task.id}-soon`,
            type: 'deadline_soon',
            taskId: task.id,
            title: 'משימה מתקרבת לדדליין',
            message: `המשימה "${task.text}" מסתיימת מחר.`,
            date: new Date().toISOString(),
            read: false
          });
        }
      }
    });
    if (newNotifs.length > 0) setNotifications(prev => [...prev, ...newNotifs]);
  }, [tasks, user, notifications]);

  // סימון התראה כנקראה
  const markNotificationRead = (notifId) => {
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
  };
  // סימון כל ההתראות כנקראו
  const markAllNotificationsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  return (
    <div className="container" dir="rtl">
      {/* Header */}
      <header className="app-header">
        <div className="app-title-container">
          <div className="app-logo">
            <div className="logo-circle">
              <span className="logo-check">✓</span>
            </div>
          </div>
          <div className="app-title-text">
            <h1 className="app-title">SEOnux</h1>
            <span className="app-version">v2.1</span>
          </div>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={toggleDarkMode}
            title={isDarkMode ? 'מצב בהיר' : 'מצב כהה'}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          {user && (
            <>
              <button className="sidebar-toggle" onClick={toggleSidebar}>
                📁 פרויקטים
              </button>
              <button className="btn btn-secondary" onClick={() => setShowStatusManager(!showStatusManager)}>
                ⚙️ ניהול סטטוסים
              </button>
              <button className="btn btn-danger" onClick={handleLogout}>
                🚪 התנתקות
              </button>
              {/* פעמון התראות */}
              <div className="notification-bell-wrapper">
                <button className="notification-bell" onClick={() => setShowNotifications(v => !v)}>
                  🛎️
                  {notifications.some(n => !n.read) && (
                    <span className="notif-badge">{notifications.filter(n => !n.read).length}</span>
                  )}
                </button>
                {showNotifications && (
                  <div className="notifications-popup">
                    <div className="notifications-header">
                      <span>התראות</span>
                      <button className="notif-markall-btn" onClick={markAllNotificationsRead}>סמן הכל כנקרא</button>
                    </div>
                    <div className="notifications-list">
                      {notifications.length === 0 ? (
                        <div className="notif-empty">אין התראות חדשות</div>
                      ) : (
                        notifications.slice().reverse().map(n => (
                          <div key={n.id} className={`notif-item ${n.read ? 'read' : ''}`}>
                            <div className="notif-title">{n.type === 'overdue' ? '🚨' : '⏰'} {n.title}</div>
                            <div className="notif-message">{n.message}</div>
                            <button className="notif-read-btn" onClick={() => markNotificationRead(n.id)} disabled={n.read}>
                              {n.read ? 'נקרא' : 'סמן כנקרא'}
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {/* App Layout */}
      {loading ? (
        <main className="main-content">
          <div className="loading">טוען...</div>
        </main>
      ) : !user ? (
        <main className="main-content">
          <div className="login-section">
            <h2>ברוכים הבאים</h2>
            <p>התחברו כדי להתחיל לנהל את המשימות שלכם</p>
            <button className="btn btn-primary" onClick={handleLogin}>
              🔐 התחברות עם Google
            </button>
          </div>
        </main>
      ) : (
        <div className="app-layout">
          {/* Sidebar Overlay for Mobile */}
          <div className={`sidebar-overlay ${sidebarOpen ? 'show' : ''}`} onClick={closeSidebar}></div>
          
          {/* Main Content Wrapper */}
          <div className="main-content-wrapper">
            <main className="main-content">
          <>
            {/* Welcome Section */}
            <div className="welcome-section">
              <div className="user-info">
                <h3>👋 שלום, {user.displayName}</h3>
                <p>{user.email} | {tasks.length} משימות פעילות</p>
                <div className="current-project-info">
                  <span className="current-project">
                    📁 פרויקט נוכחי: <strong>{getCurrentProject().name}</strong>
                  </span>
                </div>
              </div>
            </div>



            {/* Status Manager */}
            {showStatusManager && (
              <div className="status-manager">
                <div className="status-manager-header">
                  <h3>ניהול סטטוסים</h3>
                </div>
                <div className="status-manager-content">
                  <div className="add-status-form">
                    <input
                      className="form-input"
                      type="text"
                      value={newStatusName}
                      onChange={e => setNewStatusName(e.target.value)}
                      placeholder="שם הסטטוס החדש"
                    />
                    <input
                      type="color"
                      value={newStatusColor}
                      onChange={e => setNewStatusColor(e.target.value)}
                      title="בחר צבע"
                    />
                    {editingStatusId ? (
                      <>
                        <button className="btn btn-success" onClick={saveEditStatus}>שמור</button>
                        <button className="btn btn-secondary" onClick={cancelEditStatus}>ביטול</button>
                      </>
                    ) : (
                      <button className="btn btn-primary" onClick={addCustomStatus}>הוסף סטטוס</button>
                    )}
                  </div>

                  <div className="status-grid">
                    {/* סטטוסים ברירת מחדל */}
                    {defaultStatuses.map(status => (
                      <div key={status.name} className="status-item">
                        <span className="status-preview" style={{ backgroundColor: status.color }}>
                          {status.name}
                        </span>
                        <span className="status-type">ברירת מחדל</span>
                      </div>
                    ))}
                    
                    {/* סטטוסים מותאמים אישית */}
                    {customStatuses.map(status => (
                      <div key={status.id} className="status-item">
                        <span className="status-preview" style={{ backgroundColor: status.color }}>
                          {status.name}
                        </span>
                        <div className="status-actions">
                          <button onClick={() => startEditStatus(status)}>✏️</button>
                          <button onClick={() => deleteCustomStatus(status.id)}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}



            {/* Task Form */}
            <div className="task-form-container">
              <div className="task-form-header">
                <h3>יצירת משימה חדשה</h3>
              </div>
              <div className="task-form">
                <div className="form-field">
                  <label>כותרת המשימה</label>
                  <input
                    className="form-input"
                    type="text"
                    value={newTask}
                    onChange={e => setNewTask(e.target.value)}
                    placeholder="הכנס כותרת למשימה"
                  />
                </div>
                <div className="form-field">
                  <label>תאריך יעד</label>
                  <input
                    className="form-input"
                    type="date"
                    value={newDate}
                    onChange={e => setNewDate(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>סטטוס</label>
                  <div className="status-select-wrapper">
                    <div 
                      className="status-color-indicator" 
                      style={{ backgroundColor: getStatusColor(newStatus) }}
                    ></div>
                    <select
                      className="form-input status-select"
                      value={newStatus}
                      onChange={e => handleStatusChange(e.target.value)}
                    >
                      {allStatuses.map(status => (
                        <option 
                          key={status.name || status.id} 
                          value={status.name}
                        >
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label>מקטע</label>
                  <select
                    className="form-input"
                    value={newGroupId || ''}
                    onChange={e => setNewGroupId(e.target.value || null)}
                  >
                    <option value="">משימות ראשיות</option>
                    {taskGroups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>תיאור</label>
                  <textarea
                    className="form-input form-textarea"
                    value={newDescription}
                    onChange={e => setNewDescription(e.target.value)}
                    placeholder="הוסף תיאור למשימה..."
                    rows="2"
                  />
                </div>
                <div className="form-field">
                  <label>&nbsp;</label>
                  <button className="btn btn-success" onClick={addTask}>הוסף משימה</button>
                </div>
              </div>
            </div>

            {/* Tasks List */}
            <div className="tasks-container">
              <div className="tasks-header">
                {editingProjectName ? (
                  <div className="project-name-edit">
                    <input
                      className="form-input project-name-input"
                      type="text"
                      value={tempProjectName}
                      onChange={e => setTempProjectName(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter') saveProjectName();
                        if (e.key === 'Escape') cancelEditProjectName();
                      }}
                      autoFocus
                      placeholder="שם הפרויקט..."
                    />
                    <div className="project-name-actions">
                      <button className="btn btn-success btn-sm" onClick={saveProjectName}>
                        ✓ שמור
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={cancelEditProjectName}>
                        ✕ ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  <h3 className="project-title-editable" onClick={startEditProjectName} title="לחץ לעריכת שם הפרויקט">
                    📋 {projectName} ✏️
                  </h3>
                )}
                <div className="tasks-header-actions">
                  {getFilteredTasks().length > 0 && (
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={selectAllVisibleTasks}
                      title="בחר/בטל בחירה של כל המשימות הגלויות"
                    >
                      {getFilteredTasks().every(task => selectedTasks.includes(task.id)) ? '❌ בטל בחירת הכל' : '☑️ בחר הכל'}
                    </button>
                  )}
                  <button 
                    className="btn btn-success btn-sm"
                    onClick={() => setShowTemplates(true)}
                    title="יצירת פרויקט מתבנית מוכנה"
                  >
                    🎯 תבניות מוכנות
                  </button>
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setAddingNewSection(true)}
                  >
                    🟢 הוסף מקטע חדש
                  </button>
                  <span className="tasks-count">
                    {selectedTasks.length > 0 && `${selectedTasks.length} נבחרו | `}
                    {getFilteredTasks().length} מתוך {getTasksByTab(activeTab).length} ב{activeTab}
                  </span>
                </div>
              </div>

              {/* Add New Section Form */}
              {addingNewSection && (
                <div className="add-section-form">
                  <div className="section-form-content">
                    <input
                      className="form-input"
                      type="text"
                      value={newSectionName}
                      onChange={e => setNewSectionName(e.target.value)}
                      placeholder="שם המקטע החדש (לדוגמה: 'קמפיין דצמבר', 'SEO טכני'...)"
                      autoFocus
                      onKeyPress={e => e.key === 'Enter' && addNewSection()}
                    />
                    <div className="section-form-actions">
                      <button className="btn btn-success btn-sm" onClick={addNewSection}>
                        הוסף
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={cancelAddSection}>
                        ביטול
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Templates Selection */}
              {showTemplates && (
                <div className="templates-overlay">
                  <div className="templates-modal">
                    <div className="templates-header">
                      <h3>🎯 בחר תבנית מוכנה</h3>
                      <button 
                        className="btn btn-secondary btn-sm" 
                        onClick={() => setShowTemplates(false)}
                      >
                        ✕ סגור
                      </button>
                    </div>
                    <div className="templates-content">
                      {predefinedTemplates.map(template => (
                        <div key={template.id} className="template-card">
                          <div className="template-info">
                            <h4>{template.name}</h4>
                            <p>{template.description}</p>
                            <div className="template-stats">
                              <span>📋 {template.sections.length} מקטעים</span>
                              <span>✅ {template.sections.reduce((total, section) => total + section.tasks.length, 0)} משימות</span>
                            </div>
                          </div>
                          <button 
                            className="btn btn-success"
                            onClick={() => createFromTemplate(template)}
                          >
                            📥 משיכת התבנית
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Date Tabs */}
              {tasks.length > 0 && (
                <div className="date-tabs">
                  <div className="tab-buttons">
                    <button 
                      className={`tab-btn ${activeTab === 'היום' ? 'active' : ''}`}
                      onClick={() => setActiveTab('היום')}
                    >
                      🔥 היום ({getTaskCountByTab('היום')})
                    </button>
                    <button 
                      className={`tab-btn ${activeTab === 'השבוע' ? 'active' : ''}`}
                      onClick={() => setActiveTab('השבוע')}
                    >
                      📋 השבוע ({getTaskCountByTab('השבוע')})
                    </button>
                    <button 
                      className={`tab-btn ${activeTab === 'החודש הקרוב' ? 'active' : ''}`}
                      onClick={() => setActiveTab('החודש הקרוב')}
                    >
                      📆 החודש הקרוב ({getTaskCountByTab('החודש הקרוב')})
                    </button>
                    <button 
                      className={`tab-btn ${activeTab === 'חודש הבא ואילך' ? 'active' : ''}`}
                      onClick={() => setActiveTab('חודש הבא ואילך')}
                    >
                      🔮 חודש הבא ואילך ({getTaskCountByTab('חודש הבא ואילך')})
                    </button>
                  </div>
                </div>
              )}
              
              {/* Status Filter */}
              {getTasksByTab(activeTab).length > 0 && (
                <div className="status-filter">
                  <h4>סינון לפי סטטוס:</h4>
                  <div className="filter-buttons">
                    <button 
                      className={`filter-btn ${statusFilter === 'הכל' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('הכל')}
                    >
                      הכל ({getTasksByTab(activeTab).length})
                    </button>
                    {allStatuses.map(status => {
                      const count = getTasksByTab(activeTab).filter(task => task.status === status.name).length;
                      return count > 0 ? (
                        <button 
                          key={status.name}
                          className={`filter-btn ${statusFilter === status.name ? 'active' : ''}`}
                          onClick={() => setStatusFilter(status.name)}
                          style={{ 
                            backgroundColor: statusFilter === status.name ? status.color : 'transparent',
                            borderColor: status.color,
                            color: statusFilter === status.name ? '#fff' : status.color
                          }}
                        >
                          {status.name} ({count})
                        </button>
                      ) : null;
                    })}
                  </div>
                </div>
                            )}

              {/* Bulk Actions Panel */}
              {selectedTasks.length > 0 && (
                <div className="bulk-actions-panel">
                  <div className="bulk-actions-header">
                    <h4>🔧 פעולות על {selectedTasks.length} משימות נבחרות</h4>
                    <button className="btn btn-secondary btn-sm" onClick={clearSelection}>
                      ✕ בטל בחירה
                    </button>
                  </div>
                  <div className="bulk-actions-content">
                    <div className="bulk-action-group">
                      <label>שינוי סטטוס:</label>
                      <select 
                        className="form-input bulk-select"
                        onChange={(e) => e.target.value && bulkUpdateStatus(e.target.value)}
                        defaultValue=""
                      >
                        <option value="">בחר סטטוס חדש</option>
                        {allStatuses.map(status => (
                          <option key={status.name} value={status.name}>
                            {status.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="bulk-action-group">
                      <label>שינוי תאריך:</label>
                      <input 
                        type="date" 
                        className="form-input bulk-select"
                        onChange={(e) => e.target.value && bulkUpdateDate(e.target.value)}
                      />
                    </div>
                    <div className="bulk-action-group">
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={bulkDeleteTasks}
                      >
                        🗑️ מחק הכל
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {tasks.length === 0 ? (
                <div className="empty-state">
                  <h4>אין משימות עדיין</h4>
                  <p>צרו את המשימה הראשונה שלכם כדי להתחיל</p>
                </div>
              ) : getTasksByTab(activeTab).length === 0 ? (
                <div className="empty-state">
                  <h4>אין משימות ב{activeTab}</h4>
                  <p>נסו לבחור טאב אחר או ליצור משימות חדשות</p>
                </div>
              ) : getFilteredTasks().length === 0 ? (
                <div className="empty-state">
                  <h4>אין משימות ב{activeTab} עם הסטטוס "{statusFilter}"</h4>
                  <p>נסו לבחור סטטוס אחר או ליצור משימות חדשות</p>
                </div>
              ) : (
                <div className="tasks-grouped-container">
                  {Object.entries(getTasksGrouped()).map(([groupKey, group]) => 
                    (group.tasks.length > 0 || group.id !== 'main') && (
                      <div key={groupKey} className="task-group">
                        <div className="task-group-header">
                          {editingSectionId === group.id ? (
                            <div className="section-edit-form">
                              <input
                                className="form-input section-edit-input"
                                type="text"
                                value={editingSectionName}
                                onChange={e => setEditingSectionName(e.target.value)}
                                onKeyPress={e => e.key === 'Enter' && saveEditSection()}
                                autoFocus
                              />
                              <div className="section-edit-actions">
                                <button className="btn btn-success btn-sm" onClick={saveEditSection}>
                                  ✓ שמור
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={cancelEditSection}>
                                  ✕ ביטול
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="task-group-title-container">
                              <h4 className="task-group-title">
                                📋 {group.name}
                                <span className="task-group-count">({group.tasks.length})</span>
                              </h4>
                              {group.id !== 'main' && (
                                <div className="section-actions">
                                  <button 
                                    className="section-action-btn edit-btn"
                                    onClick={() => startEditSection(group)}
                                    title="ערוך מקטע"
                                  >
                                    ✏️
                                  </button>
                                  <button 
                                    className="section-action-btn delete-btn"
                                    onClick={() => deleteSection(group.id)}
                                    title="מחק מקטע"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {group.tasks.length > 0 ? (
                          <table className="tasks-table">
                            <thead>
                                            <tr>
                <th style={{ width: '30px' }}>☑️</th>
                <th>משימה</th>
                <th style={{ width: '120px' }}>סטטוס</th>
                <th style={{ width: '120px' }}>תאריך יעד</th>
                <th style={{ width: '100px' }}>פעולות</th>
              </tr>
                            </thead>
                            <tbody>
                              {group.tasks.map(task => (
                      <React.Fragment key={task.id}>
                        <tr className={`task-row ${isTaskOverdue(task.dueDate) ? 'overdue' : ''} ${selectedTasks.includes(task.id) ? 'selected' : ''}`}>
                          {editingId === task.id ? (
                            <td colSpan="5">
                              <div className="edit-form">
                                <div className="edit-form-grid">
                                  <div className="edit-field">
                                    <label>כותרת המשימה</label>
                                    <input
                                      className="form-input"
                                      type="text"
                                      value={editData.text}
                                      onChange={e => setEditData({ ...editData, text: e.target.value })}
                                    />
                                  </div>
                                  <div className="edit-field">
                                    <label>תאריך יעד</label>
                                    <input
                                      className="form-input"
                                      type="date"
                                      value={editData.dueDate}
                                      onChange={e => setEditData({ ...editData, dueDate: e.target.value })}
                                    />
                                  </div>
                                  <div className="edit-field">
                                    <label>סטטוס</label>
                                    <div className="status-select-wrapper">
                                      <div 
                                        className="status-color-indicator" 
                                        style={{ backgroundColor: getStatusColor(editData.status || defaultStatuses[0].name) }}
                                      ></div>
                                      <select
                                        className="form-input status-select"
                                        value={editData.status || defaultStatuses[0].name}
                                        onChange={e => setEditData({ ...editData, status: e.target.value })}
                                      >
                                        {allStatuses.map(status => (
                                          <option 
                                            key={status.name || status.id} 
                                            value={status.name}
                                          >
                                            {status.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                  <div className="edit-field full-width">
                                    <label>תיאור</label>
                                    <textarea
                                      className="form-input form-textarea"
                                      value={editData.description || ''}
                                      onChange={e => setEditData({ ...editData, description: e.target.value })}
                                      placeholder="תיאור המשימה..."
                                      rows="3"
                                    />
                                  </div>
                                </div>
                                <div className="edit-actions">
                                  <button className="btn btn-success" onClick={saveEdit}>שמור</button>
                                  <button className="btn btn-secondary" onClick={cancelEdit}>ביטול</button>
                                </div>
                              </div>
                            </td>
                          ) : (
                            <>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedTasks.includes(task.id)}
                                  onChange={() => toggleSelectTask(task.id)}
                                  className="task-select-checkbox"
                                />
                              </td>
                              <td 
                                className="task-content-cell" 
                                onClick={() => toggleTaskExpansion(task.id)}
                                style={{ cursor: 'pointer' }}
                              >
                                <div className="task-title">{task.text}</div>
                                {task.description && (
                                  <div className="task-description">
                                    {truncateDescription(task.description)}
                                    {task.description.split(' ').length > 5 && (
                                      <span className="expand-indicator">
                                        {expandedTaskId === task.id ? ' (לחץ לסגירה)' : ' (לחץ להרחבה)'}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td>
                                <span 
                                  className="status-preview" 
                                  style={{ backgroundColor: getStatusColor(task.status || defaultStatuses[0].name) }}
                                >
                                  {task.status || defaultStatuses[0].name}
                                </span>
                              </td>
                              <td>
                                <div className={`task-date ${isTaskOverdue(task.dueDate) ? 'overdue' : ''}`}>
                                  {isTaskOverdue(task.dueDate) && (
                                    <span className="overdue-icon" title={`באיחור ${getOverdueDays(task.dueDate)} ימים`}>
                                      🚨
                                    </span>
                                  )}
                                  {task.dueDate || 'ללא תאריך'}
                                </div>
                              </td>
                              <td>
                                <div className="task-actions">
                                  <button onClick={(e) => {e.stopPropagation(); startEdit(task)}}>✏️</button>
                                  <button onClick={(e) => {e.stopPropagation(); deleteTask(task.id)}}>🗑️</button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                        {/* אקורדיון עם תיאור מלא ותת-משימות */}
                        {expandedTaskId === task.id && (
                          <tr className="task-expanded-row">
                            <td colSpan="5">
                              <div className="task-expanded-content">
                                {/* תיאור מלא */}
                                {task.description && task.description.split(' ').length > 5 && (
                                  <div className="expanded-description">
                                    <strong>תיאור מלא:</strong>
                                    <p>{task.description}</p>
                                  </div>
                                )}
                                
                                {/* תת-משימות */}
                                <div className="subtasks-section">
                                  <div className="subtasks-header">
                                    <strong>תת-משימות</strong>
                                    <button 
                                      className="add-subtask-btn"
                                      onClick={() => startAddSubtask(task.id)}
                                      title="הוסף תת משימה"
                                    >
                                      ➕
                                    </button>
                                  </div>
                                  
                                  {/* טופס הוספת תת-משימה */}
                                  {addingSubtaskToId === task.id && (
                                    <div className="add-subtask-form">
                                      <div className="subtask-form-grid">
                                        <div className="form-field">
                                          <label>כותרת התת-משימה</label>
                                          <input
                                            className="form-input"
                                            type="text"
                                            value={newSubtaskText}
                                            onChange={(e) => setNewSubtaskText(e.target.value)}
                                            placeholder="הכנס תת-משימה חדשה..."
                                            autoFocus
                                            onKeyPress={(e) => e.key === 'Enter' && addSubtask(task.id)}
                                          />
                                        </div>
                                        <div className="form-field">
                                          <label>תאריך יעד</label>
                                          <input
                                            className="form-input"
                                            type="date"
                                            value={newSubtaskDate}
                                            onChange={(e) => setNewSubtaskDate(e.target.value)}
                                          />
                                        </div>
                                        <div className="form-field">
                                          <label>סטטוס</label>
                                          <div className="status-select-wrapper">
                                            <div 
                                              className="status-color-indicator" 
                                              style={{ backgroundColor: getStatusColor(newSubtaskStatus) }}
                                            ></div>
                                            <select
                                              className="form-input status-select"
                                              value={newSubtaskStatus}
                                              onChange={(e) => setNewSubtaskStatus(e.target.value)}
                                            >
                                              {allStatuses.map(status => (
                                                <option 
                                                  key={status.name || status.id} 
                                                  value={status.name}
                                                >
                                                  {status.name}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        </div>
                                        <div className="form-field full-width">
                                          <label>תיאור</label>
                                          <textarea
                                            className="form-input form-textarea"
                                            value={newSubtaskDescription}
                                            onChange={(e) => setNewSubtaskDescription(e.target.value)}
                                            placeholder="הוסף תיאור לתת-משימה..."
                                            rows="2"
                                          />
                                        </div>
                                      </div>
                                      <div className="subtask-form-actions">
                                        <button className="btn btn-success btn-sm" onClick={() => addSubtask(task.id)}>
                                          הוסף
                                        </button>
                                        <button className="btn btn-secondary btn-sm" onClick={cancelAddSubtask}>
                                          ביטול
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* רשימת תת-משימות */}
                                  {task.subtasks && task.subtasks.length > 0 && (
                                    <div className="subtasks-list">
                                      {task.subtasks.map(subtask => (
                                        <div key={subtask.id} className="subtask-item">
                                          {editingSubtaskId === subtask.id ? (
                                            <div className="subtask-edit-form">
                                              <div className="subtask-form-grid">
                                                <div className="form-field">
                                                  <label>כותרת התת-משימה</label>
                                                  <input
                                                    className="form-input"
                                                    type="text"
                                                    value={editSubtaskData.text || ''}
                                                    onChange={(e) => setEditSubtaskData({ ...editSubtaskData, text: e.target.value })}
                                                  />
                                                </div>
                                                <div className="form-field">
                                                  <label>תאריך יעד</label>
                                                  <input
                                                    className="form-input"
                                                    type="date"
                                                    value={editSubtaskData.dueDate || todayDate}
                                                    onChange={(e) => setEditSubtaskData({ ...editSubtaskData, dueDate: e.target.value })}
                                                  />
                                                </div>
                                                <div className="form-field">
                                                  <label>סטטוס</label>
                                                  <div className="status-select-wrapper">
                                                    <div 
                                                      className="status-color-indicator" 
                                                      style={{ backgroundColor: getStatusColor(editSubtaskData.status || defaultStatuses[0].name) }}
                                                    ></div>
                                                    <select
                                                      className="form-input status-select"
                                                      value={editSubtaskData.status || defaultStatuses[0].name}
                                                      onChange={(e) => setEditSubtaskData({ ...editSubtaskData, status: e.target.value })}
                                                    >
                                                      {allStatuses.map(status => (
                                                        <option 
                                                          key={status.name || status.id} 
                                                          value={status.name}
                                                        >
                                                          {status.name}
                                                        </option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                </div>
                                                <div className="form-field full-width">
                                                  <label>תיאור</label>
                                                  <textarea
                                                    className="form-input form-textarea"
                                                    value={editSubtaskData.description || ''}
                                                    onChange={(e) => setEditSubtaskData({ ...editSubtaskData, description: e.target.value })}
                                                    placeholder="תיאור התת-משימה..."
                                                    rows="2"
                                                  />
                                                </div>
                                              </div>
                                              <div className="subtask-form-actions">
                                                <button className="btn btn-success btn-sm" onClick={saveEditSubtask}>
                                                  שמור
                                                </button>
                                                <button className="btn btn-secondary btn-sm" onClick={cancelEditSubtask}>
                                                  ביטול
                                                </button>
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              <div className="subtask-content">
                                                <input
                                                  type="checkbox"
                                                  checked={subtask.done}
                                                  onChange={() => toggleSubtask(task.id, subtask.id)}
                                                  className="subtask-checkbox"
                                                />
                                                <div className="subtask-details">
                                                  <div className="subtask-header">
                                                    <span className={`subtask-text ${subtask.done ? 'completed' : ''}`}>
                                                      {subtask.text}
                                                    </span>
                                                    <div className="subtask-meta">
                                                      <span 
                                                        className="status-preview status-mini" 
                                                        style={{ backgroundColor: getStatusColor(subtask.status || defaultStatuses[0].name) }}
                                                      >
                                                        {subtask.status || defaultStatuses[0].name}
                                                      </span>
                                                      <span className="subtask-date">{subtask.dueDate || todayDate}</span>
                                                    </div>
                                                  </div>
                                                  {subtask.description && (
                                                    <div className="subtask-description">
                                                      {subtask.description}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="subtask-actions">
                                                <button 
                                                  className="edit-subtask-btn"
                                                  onClick={() => startEditSubtask(task.id, subtask)}
                                                  title="ערוך תת-משימה"
                                                >
                                                  ✏️
                                                </button>
                                                <button 
                                                  className="delete-subtask-btn"
                                                  onClick={() => deleteSubtask(task.id, subtask.id)}
                                                  title="מחק תת-משימה"
                                                >
                                                  🗑️
                                                </button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* הודעה כשאין תת-משימות */}
                                  {(!task.subtasks || task.subtasks.length === 0) && addingSubtaskToId !== task.id && (
                                    <div className="no-subtasks">
                                      <p>אין תת-משימות עדיין. לחץ על ➕ כדי להוסיף.</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                          </tbody>
                        </table>
                        ) : (
                          <div className="empty-state">
                            <h4>המקטע ריק</h4>
                            <p>הוסף משימות למקטע זה או מחק אותו</p>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </>
            </main>
          </div>
          
          {/* Projects Sidebar */}
          <div className={`projects-sidebar ${sidebarOpen ? 'open' : ''}`}>
            <div className="projects-sidebar-header">
              <h3>הפרויקטים שלי</h3>
            </div>
            <div className="projects-sidebar-content">
              {/* Create New Project */}
              <div className={`sidebar-create-project ${creatingProject ? 'expanded' : ''}`}>
                {creatingProject ? (
                  <div className="sidebar-create-form">
                    <input
                      className="form-input"
                      type="text"
                      value={newProjectName}
                      onChange={e => setNewProjectName(e.target.value)}
                      placeholder="שם הפרויקט החדש..."
                      autoFocus
                      onKeyPress={e => {
                        if (e.key === 'Enter') createNewProject();
                        if (e.key === 'Escape') cancelCreatingProject();
                      }}
                    />
                    <div className="sidebar-create-actions">
                      <button className="btn btn-success" onClick={createNewProject}>
                        ✓ יצור
                      </button>
                      <button className="btn btn-secondary" onClick={cancelCreatingProject}>
                        ✕ ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="create-project-trigger" onClick={startCreatingProject}>
                    <span style={{ fontSize: '18px' }}>+</span>
                    <span>פרויקט חדש</span>
                  </div>
                )}
              </div>

              {/* Default Project */}
              <div 
                className={`sidebar-project-item ${currentProjectId === 'default' ? 'active' : ''}`}
                onClick={() => switchToProject('default')}
              >
                <div className="sidebar-project-icon">◈</div>
                <div className="sidebar-project-details">
                  <div className="sidebar-project-name">
                    {currentProjectId === 'default' ? projectName : 'פרויקט ראשי'}
                    {currentProjectId === 'default' && <span style={{ opacity: 0.6, fontSize: '0.8em' }}> ✏️</span>}
                  </div>
                  <div className="sidebar-project-stats">
                    ● {getProjectTaskCount('default')} משימות
                  </div>
                </div>
                {currentProjectId === 'default' && (
                  <div className="sidebar-project-actions">
                    <button 
                      className="sidebar-action-btn edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditProjectName();
                      }}
                      title="ערוך שם פרויקט"
                    >
                      ✏️
                    </button>
                  </div>
                )}
              </div>

              {/* Custom Projects */}
              {projects.map((project, index) => (
                <div 
                  key={project.id}
                  className={`sidebar-project-item ${currentProjectId === project.id ? 'active' : ''} ${editingProjectId === project.id ? 'editing' : ''}`}
                  onClick={editingProjectId === project.id ? undefined : () => switchToProject(project.id)}
                >
                  {editingProjectId === project.id ? (
                    <div className="sidebar-project-edit-form">
                      <input
                        className="form-input sidebar-project-edit-input"
                        type="text"
                        value={editingProjectNameTemp}
                        onChange={e => setEditingProjectNameTemp(e.target.value)}
                        onKeyPress={e => {
                          if (e.key === 'Enter') saveSidebarProjectName();
                          if (e.key === 'Escape') cancelEditSidebarProject();
                        }}
                        autoFocus
                        onClick={e => e.stopPropagation()}
                      />
                      <div className="sidebar-project-edit-actions">
                        <button 
                          className="btn btn-success btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            saveSidebarProjectName();
                          }}
                        >
                          ✓
                        </button>
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelEditSidebarProject();
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div 
                        className="sidebar-project-icon"
                        style={{ 
                          backgroundColor: currentProjectId === project.id ? getProjectColor(index) : 'var(--bg-accent)',
                          color: currentProjectId === project.id ? 'white' : 'var(--text-secondary)'
                        }}
                      >
                        {String.fromCharCode(65 + (index % 26))}
                      </div>
                      <div className="sidebar-project-details">
                        <div className="sidebar-project-name">{project.name}</div>
                        <div className="sidebar-project-stats">
                          ● {getProjectTaskCount(project.id)} משימות
                        </div>
                      </div>
                      <div className="sidebar-project-actions">
                        <button 
                          className="sidebar-action-btn edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditSidebarProject(project);
                          }}
                          title="ערוך שם פרויקט"
                        >
                          ✏️
                        </button>
                        <button 
                          className="sidebar-action-btn delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteProject(project.id);
                          }}
                          title="מחק פרויקט"
                        >
                          ×
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {projects.length === 0 && !creatingProject && (
                <div className="no-projects" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <p>לחץ על "➕ פרויקט חדש" כדי ליצור פרויקט ראשון!</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
    </div>
  );
}

export default App;
